/**
 * Admin Governance Engine
 * Single file — 6 governance modules:
 *
 *  /admin/dashboard          — KPIs + Action Required + Platform Snapshot
 *  /admin/users              — Pending Approvals + Active Users + Create User
 *  /admin/enterprise-pipeline — Institutional leads (formerly CRM)
 *  /admin/deals              — Deal & Program Oversight
 *  /admin/quality            — Data Quality Control
 *  /admin/system             — System Configuration
 *
 * Design: control tower, not dashboard.
 * Actions: approve / validate / monitor / intervene.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, NavLink, Outlet, Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import ClientActivationPage from "./ClientActivationPage";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  LayoutDashboard, Users, Building2, Layers, Shield, Settings,
  RefreshCw, CheckCircle2, AlertTriangle, X, MessageSquare,
  TrendingDown, DollarSign, ArrowRight, ChevronRight, BarChart2,
  Clock, Target, Zap, Info, Edit3, Lock
} from "lucide-react";

// ── Shared: Admin Nav ─────────────────────────────────────────────────────
const NAV_ITEMS = [
  { path: "/admin/dashboard",           icon: LayoutDashboard, label: "Dashboard",              badge: null    },
  { path: "/admin/users",               icon: Users,           label: "User Management",         badge: "queue" },
  { path: "/admin/onboarding",          icon: Zap,             label: "Client Activation",       badge: null    },
  { path: "/admin/enterprise-pipeline", icon: Building2,       label: "Enterprise Pipeline",     badge: null    },
  { path: "/admin/deals",               icon: Layers,          label: "Deal Oversight",          badge: null    },
  { path: "/admin/quality",             icon: Shield,          label: "Quality Control",         badge: null    },
  { path: "/admin/audit-log",           icon: Clock,           label: "Audit Log",               badge: null    },
  { path: "/admin/system",              icon: Settings,        label: "System Config",           badge: null    },
];

function AdminNav({ kpis }) {
  const navigate = useNavigate();
  const loc      = useLocation();
  return (
    <div className="w-[220px] flex-shrink-0 bg-[#080E1A] border-r border-white/8 h-full overflow-y-auto sticky top-0">
      {/* Header */}
      <div className="px-5 py-5 border-b border-white/8">
        <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-0.5">DeCarbonIQ</p>
        <p className="text-sm font-bold text-white">Admin Control Tower</p>
        {kpis?.alerts > 0 && (
          <div className="mt-2 flex items-center gap-1.5 text-xs font-bold text-amber-400">
            <AlertTriangle size={11} /> {kpis.alerts} action{kpis.alerts !== 1 ? "s" : ""} required
          </div>
        )}
      </div>
      <div className="p-3">
        {NAV_ITEMS.map(item => {
          const Icon    = item.icon;
          const isActive = loc.pathname.startsWith(item.path);
          const count   = item.badge === "queue" ? kpis?.pending_users : null;
          return (
            <button key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium text-left transition-all mb-0.5 ${
                isActive ? "bg-white/10 text-white" : "text-slate-500 hover:bg-white/5 hover:text-slate-300"
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g,"-")}`}>
              <Icon size={13} className={isActive ? "text-teal-400" : "text-slate-600"} />
              <span className="flex-1">{item.label}</span>
              {count > 0 && (
                <span className="text-xs font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full">{count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Shared: KPI card ──────────────────────────────────────────────────────
function KPI({ label, value, sub, alert, color = "text-slate-900" }) {
  return (
    <MetricCard label={label} value={value} sub={sub} color={color} />
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 1: Dashboard
// ═══════════════════════════════════════════════════════════════════════════
function AdminDashboard({ kpis, snapshot, deals }) {
  const navigate = useNavigate();
  if (!kpis) return <Loader />;

  const flagged   = (deals || []).filter(d => d.is_flagged);
  const pendingFr = (deals || []).filter(d => d.status === "submitted");

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <PageHeader title="Admin Dashboard" />
        <p className="text-[11px] text-slate-500 mt-0.5">Platform health + immediate actions. Updated on load.</p>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI label="Pending Users"     value={kpis.pending_users}  alert={kpis.pending_users > 0} color="text-amber-400" />
        <KPI label="Active Deals"      value={kpis.active_deals}   color="text-white" />
        <KPI label="Qualified Deals"   value={kpis.qualified_deals}color="text-teal-400" />
        <KPI label="Programs"          value={kpis.programs}       color="text-white" />
        <KPI label="Alerts"            value={kpis.alerts}         alert={kpis.alerts > 0} color="text-amber-400" />
      </div>

      {/* Action Required */}
      <div className="bg-[#0B1220] border border-white/8 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={14} className="text-amber-400" />
          <p className="text-sm font-bold text-white">Action Required</p>
          {kpis.alerts > 0 && <span className="text-xs font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">{kpis.alerts}</span>}
        </div>
        <div className="space-y-2">
          {kpis.pending_users > 0 && (
            <button onClick={() => navigate("/admin/users")}
              className="w-full flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 text-left hover:bg-amber-500/15 transition-colors"
              data-testid="action-pending-users">
              <Clock size={14} className="text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-bold text-amber-300">{kpis.pending_users} user{kpis.pending_users !== 1 ? "s" : ""} awaiting approval</p>
                <p className="text-xs text-amber-600">Review and approve or reject registrations</p>
              </div>
              <ChevronRight size={13} className="text-amber-500" />
            </button>
          )}
          {flagged.length > 0 && (
            <button onClick={() => navigate("/admin/deals")}
              className="w-full flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-left hover:bg-red-500/15 transition-colors"
              data-testid="action-flagged-deals">
              <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-bold text-red-300">{flagged.length} deal{flagged.length !== 1 ? "s" : ""} flagged (missing data)</p>
                <p className="text-xs text-red-600">Review before FI pipeline visibility</p>
              </div>
              <ChevronRight size={13} className="text-red-500" />
            </button>
          )}
          {pendingFr.length > 0 && (
            <button onClick={() => navigate("/admin/deals")}
              className="w-full flex items-center gap-3 bg-white/5 border border-white/8 rounded-lg px-4 py-3 text-left hover:bg-white/8 transition-colors"
              data-testid="action-pending-deals">
              <DollarSign size={14} className="text-teal-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-bold text-white">{pendingFr.length} deal{pendingFr.length !== 1 ? "s" : ""} submitted for review</p>
                <p className="text-xs text-slate-500">Validate before routing to FI pipeline</p>
              </div>
              <ChevronRight size={13} className="text-slate-500" />
            </button>
          )}
          {kpis.alerts === 0 && flagged.length === 0 && pendingFr.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-slate-500">
              <CheckCircle2 size={13} className="text-teal-500" /> No immediate actions required
            </div>
          )}
        </div>
      </div>

      {/* Platform Snapshot */}
      {snapshot && (
        <div className="bg-[#0B1220] border border-white/8 rounded-xl p-5">
          <p className="text-sm font-bold text-white mb-4">Platform Snapshot</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["SME Users",      snapshot.users_by_role?.sme        || 0, "text-teal-400"],
              ["Corporate",      snapshot.users_by_role?.corporate   || 0, "text-blue-400"],
              ["Project Dev",    snapshot.users_by_role?.project     || 0, "text-purple-400"],
              ["FI Users",       snapshot.users_by_role?.fi          || 0, "text-amber-400"],
              ["Total Deals",    snapshot.total_deals,               "text-white"],
              ["CAPEX Pipeline", `$${((snapshot.total_capex_usd||0)/1000000).toFixed(1)}M`, "text-emerald-400"],
              ["CO₂ Tracked",    `${snapshot.total_reduction_tco2e}t`, "text-teal-400"],
              ["Ready Projects", snapshot.projects_ready || 0,       "text-white"],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-white/4 rounded-lg px-3 py-2.5">
                <p className={`text-lg font-bold ${c}`}>{v}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">{l}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 2: User Management
// ═══════════════════════════════════════════════════════════════════════════
function AdminUsers() {
  const [tab,    setTab]    = useState("pending");
  const [users,  setUsers]  = useState([]);
  const [total,  setTotal]  = useState(0);
  const [search, setSearch] = useState("");
  const [role,   setRole]   = useState("");
  const [busy,   setBusy]   = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab === "pending") params.set("status", "pending_approval");
      else if (tab === "active") params.set("status", "active");
      if (search) params.set("search", search);
      if (role)   params.set("role", role);
      const res = await client.get(`/admin-gov/users?${params}`);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [tab, search, role]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (userId, action, note = "") => {
    setBusy(userId + action);
    try {
      if (action === "approve" || action === "reject" || action === "request_info") {
        await client.post("/onboarding/admin/action", { user_id: userId, action, note });
      } else {
        await client.post(`/admin-gov/users/${userId}/action`, { action });
      }
      toast.success({ approve: "Approved", reject: "Rejected", suspend: "Suspended", activate: "Activated", request_info: "Info requested" }[action] || "Done");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed."); }
    setBusy(null);
  };

  const ROLE_BADGE = { sme: "bg-teal-900/40 text-teal-400", corporate: "bg-blue-900/40 text-blue-400", project: "bg-purple-900/40 text-purple-400", fi: "bg-amber-900/40 text-amber-400" };
  const STATUS_COLOR = { active: "text-emerald-400", pending_approval: "text-amber-400", rejected: "text-red-400", suspended: "text-slate-500" };

  return (
    <div className="space-y-5" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">User Management</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">Approve registrations, manage access, create users.</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-700"
          data-testid="toggle-create-user">
          <Users size={12} /> Create User
        </button>
      </div>

      {/* Create User Form (reuse from PendingRegistrations) */}
      {showCreate && (
        <div className="bg-[#0B1220] border border-white/8 rounded-xl p-5">
          <AdminCreateUserInline onCreated={load} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-[#0B1220] border border-white/8 rounded-xl p-1 self-start w-fit">
        {[["pending","Pending Approval"],["active","Active"],["all","All Users"]].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${tab===v ? "bg-white/10 text-white" : "text-slate-500 hover:text-white"}`}
            data-testid={`user-tab-${v}`}>{l}</button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name or email..."
          className="h-9 px-3 bg-[#0B1220] border border-white/10 rounded-lg text-xs text-white placeholder-slate-600 focus:outline-none focus:border-teal-500 w-64" />
        <select value={role} onChange={e => setRole(e.target.value)}
          className="h-9 px-3 bg-[#0B1220] border border-white/10 rounded-lg text-xs text-white focus:outline-none">
          <option value="">All roles</option>
          {["sme","corporate","project","fi"].map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
        </select>
      </div>

      {/* User list */}
      {loading ? <Loader /> : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">{total} user{total !== 1 ? "s" : ""}</p>
          {users.map(u => (
            <div key={u.id} className={`bg-[#0B1220] border rounded-xl px-5 py-3 flex items-start gap-4 ${
              u.account_status === "pending_approval" ? "border-amber-500/30" : "border-white/8"
            }`} data-testid={`user-row-${u.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-xs font-bold text-white">{u.first_name} {u.last_name}</p>
                  {u.active_role && <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${ROLE_BADGE[u.active_role] || "bg-white/10 text-white"}`}>{u.active_role.toUpperCase()}</span>}
                  <span className={`text-xs font-semibold capitalize ${STATUS_COLOR[u.account_status] || "text-slate-400"}`}>{u.account_status?.replace(/_/g," ")}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span>{u.email}</span>
                  {u.org_name && u.org_name !== "—" && <span>{u.org_name}</span>}
                  {u.org_country && u.org_country !== "—" && <span>{u.org_country}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {u.account_status === "pending_approval" && (
                  <>
                    <button onClick={() => handleAction(u.id, "approve")} disabled={!!busy}
                      className="text-xs font-bold px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40"
                      data-testid={`approve-${u.id}`}>
                      {busy === u.id + "approve" ? <RefreshCw size={10} className="animate-spin" /> : "Approve"}
                    </button>
                    <button onClick={() => handleAction(u.id, "reject")} disabled={!!busy}
                      className="text-xs font-bold px-3 py-1.5 border border-red-500/40 text-red-400 rounded-lg hover:bg-red-500/10 disabled:opacity-40">
                      Reject
                    </button>
                  </>
                )}
                {u.account_status === "active" && u.id !== 1 && (
                  <button onClick={() => handleAction(u.id, "suspend")} disabled={!!busy}
                    className="text-xs font-semibold px-3 py-1.5 border border-white/10 text-slate-400 rounded-lg hover:bg-white/5 disabled:opacity-40">
                    Suspend
                  </button>
                )}
                {u.account_status === "suspended" && (
                  <button onClick={() => handleAction(u.id, "activate")} disabled={!!busy}
                    className="text-xs font-bold px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40">
                    Reactivate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Inline create user form for admin users page
function AdminCreateUserInline({ onCreated }) {
  const [form, setForm] = useState({ first_name:"", last_name:"", email:"", org_name:"", role:"sme", country:"", send_invite:true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const handleCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await client.post("/onboarding/admin/create-user", form);
      setResult(res.data);
      toast.success(`User created: ${res.data.email}`);
      if (onCreated) onCreated();
      setForm({ first_name:"",last_name:"",email:"",org_name:"",role:"sme",country:"",send_invite:true });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
    setBusy(false);
  };
  return (
    <form onSubmit={handleCreate} className="space-y-3">
      <p className="text-xs font-bold text-teal-400 uppercase tracking-widest mb-3">Create User (Admin Path)</p>
      <div className="grid grid-cols-2 gap-4">
        {[["first_name","First Name"],["last_name","Last Name"]].map(([k,l]) => (
          <input key={k} value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={l} required
            className="h-9 px-3 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-500" />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <input value={form.email} onChange={e=>set("email",e.target.value)} placeholder="Work email" type="email" required
          className="h-9 px-3 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
          data-testid="create-email" />
        <input value={form.org_name} onChange={e=>set("org_name",e.target.value)} placeholder="Organization" required
          className="h-9 px-3 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-500" />
        <input value={form.country} onChange={e=>set("country",e.target.value)} placeholder="Country"
          className="h-9 px-3 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-500" />
      </div>
      <div className="flex items-center gap-3">
        <select value={form.role} onChange={e=>set("role",e.target.value)}
          className="h-9 px-3 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none" data-testid="create-role">
          {["sme","corporate","project","fi"].map(r=><option key={r} value={r} className="text-slate-800">{r.toUpperCase()}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={form.send_invite} onChange={e=>set("send_invite",e.target.checked)} className="rounded" />
          Send invite email
        </label>
        <button type="submit" disabled={busy}
          className="px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-700 disabled:opacity-40 flex items-center gap-2"
          data-testid="submit-create-btn">
          {busy ? <RefreshCw size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
          {busy ? "Creating..." : "Create"}
        </button>
      </div>
      {result && (
        <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3 text-xs">
          <p className="text-emerald-400 font-bold">{result.email} created · {result.email_sent ? "Invite sent ✓" : "Copy invite URL:"}</p>
          {!result.email_sent && <p className="text-slate-400 font-mono mt-1 break-all">{result.invite_url}</p>}
        </div>
      )}
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 3: Enterprise Pipeline — Full stages
// ═══════════════════════════════════════════════════════════════════════════
const PIPELINE_STAGES = [
  { key: "new",       label: "New",       color: "bg-slate-800 text-slate-300"   },
  { key: "assigned",  label: "Assigned",  color: "bg-blue-900/40 text-blue-400"  },
  { key: "engaged",   label: "Engaged",   color: "bg-teal-900/40 text-teal-400"  },
  { key: "onboarded", label: "Onboarded", color: "bg-emerald-900/40 text-emerald-400" },
  { key: "active",    label: "Active",    color: "bg-teal-600/30 text-teal-300"  },
  { key: "converted", label: "Converted", color: "bg-emerald-600/30 text-emerald-300" },
];

function AdminEnterprisePipeline() {
  const navigate = useNavigate();
  const [data,    setData]    = useState(null);
  const [stage,   setStage]   = useState("");
  const [busy,    setBusy]    = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await client.get(`/admin-gov/enterprise-pipeline${stage ? `?stage=${stage}` : ""}`);
      setData(res.data);
    } catch {}
  }, [stage]);

  useEffect(() => { load(); }, [load]);

  const advanceStage = async (inquiryId, newStage, assignTo = "") => {
    setBusy(inquiryId);
    try {
      await client.post(`/admin-gov/enterprise-pipeline/${inquiryId}/stage`, {
        stage: newStage, assigned_to: assignTo || undefined,
      });
      toast.success(`Stage → ${newStage}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
    setBusy(null);
  };

  const NEXT_STAGE = { new:"assigned", assigned:"engaged", engaged:"onboarded", onboarded:"active", active:"converted" };

  return (
    <div className="space-y-5" data-testid="admin-enterprise-pipeline">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Enterprise Pipeline</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">Institutional leads: Interest → Onboarding → Conversion</p>
        </div>
        <button onClick={() => navigate("/academy?mode=enterprise")}
          className="text-xs font-semibold text-teal-400 border border-teal-500/30 px-3 py-2 rounded-lg hover:bg-teal-500/10">
          New Lead Form →
        </button>
      </div>

      {/* Stage summary */}
      {data && (
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
          {PIPELINE_STAGES.map(s => (
            <button key={s.key} onClick={() => setStage(stage === s.key ? "" : s.key)}
              className={`rounded-xl px-3 py-2.5 text-center border transition-all ${
                stage === s.key ? "border-teal-400/50 bg-white/5" : "border-white/8 bg-[#0B1220]"
              }`}>
              <p className={`text-lg font-bold ${s.color.split(" ")[1]}`}>{data.by_stage?.[s.key] || 0}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wide">{s.label}</p>
            </button>
          ))}
        </div>
      )}

      {/* Lead list */}
      <div className="space-y-2">
        {(data?.inquiries || []).map(inq => {
          const stageCfg = PIPELINE_STAGES.find(s => s.key === inq.stage) || PIPELINE_STAGES[0];
          const nextStage = NEXT_STAGE[inq.stage];
          return (
            <div key={inq.id} className="bg-[#0B1220] border border-white/8 rounded-xl px-5 py-3 flex items-center gap-4"
              data-testid={`pipeline-row-${inq.inquiry_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-xs font-bold text-white truncate">{inq.organization}</p>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${stageCfg.color}`}>{stageCfg.label}</span>
                  {inq.qualification_tier && <span className="text-xs text-teal-400">{inq.qualification_tier}</span>}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span>{inq.contact_email}</span>
                  {inq.assigned_to && <span>→ {inq.assigned_to}</span>}
                  {inq.created_at && <span>{new Date(inq.created_at).toLocaleDateString()}</span>}
                </div>
              </div>
              {nextStage && (
                <button onClick={() => advanceStage(inq.inquiry_id, nextStage)}
                  disabled={busy === inq.inquiry_id}
                  className="flex-shrink-0 text-xs font-bold text-teal-400 border border-teal-500/30 px-3 py-1.5 rounded-lg hover:bg-teal-500/10 disabled:opacity-40"
                  data-testid={`advance-${inq.inquiry_id}`}>
                  {busy === inq.inquiry_id ? <RefreshCw size={10} className="animate-spin" /> : `→ ${nextStage}`}
                </button>
              )}
            </div>
          );
        })}
        {(data?.inquiries || []).length === 0 && (
          <p className="text-xs text-slate-500 py-4">No inquiries in pipeline{stage ? ` at stage "${stage}"` : ""}.</p>
        )}
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// PAGE 4: Deal Oversight — Risk Score + Admin Clearance Gate
// ═══════════════════════════════════════════════════════════════════════════
function DealRiskBadge({ requestId }) {
  const [risk, setRisk] = useState(null);
  useEffect(() => {
    client.get(`/admin-gov/deals/${requestId}/risk-score`).then(r => setRisk(r.data)).catch(() => {});
  }, [requestId]);
  if (!risk) return null;
  const VC = { cleared: "text-emerald-400", review_required: "text-amber-400", flagged: "text-red-400" };
  return (
    <div className="mt-3 bg-[#0F172A] rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500 uppercase tracking-wide">Risk Assessment</p>
        <span className={`text-xs font-bold ${VC[risk.verdict] || "text-slate-400"}`}>
          {risk.verdict?.replace(/_/g," ")} · {risk.total}/100
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        {Object.entries(risk.dimensions || {}).map(([key, dim]) => (
          <div key={key} className="text-center">
            <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-0.5">
              <div className="h-full bg-teal-500 rounded-full" style={{ width: `${(dim.score/dim.max)*100}%` }} />
            </div>
            <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g," ")}</p>
            <p className="text-xs font-bold text-white">{dim.score}/{dim.max}</p>
          </div>
        ))}
      </div>
      {risk.flags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {risk.flags.map(f => <span key={f} className="text-xs font-bold text-amber-400 bg-amber-900/20 px-1.5 py-0.5 rounded">⚠ {f.replace(/_/g," ")}</span>)}
        </div>
      )}
    </div>
  );
}

function AdminDeals() {
  const [deals,    setDeals]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [acting,   setActing]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const flagged = filter === "flagged" ? "&flagged_only=true" : "";
      const ws      = !["all","flagged"].includes(filter) ? `&workspace=${filter}` : "";
      const res = await client.get(`/admin-gov/deals?${flagged}${ws}`);
      setDeals(res.data.deals || []);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (requestId, action) => {
    setActing(requestId + action);
    try {
      await client.post(`/admin-gov/deals/${requestId}/action`, { action });
      toast.success({ clear: "Deal cleared for FI pipeline ✓", hold: "Deal placed on hold", request_revision: "Revision requested" }[action] || "Done");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed."); }
    setActing(null);
  };

  const SIGNAL_COLOR = { "High Priority": "text-emerald-400", "Medium Priority": "text-amber-400", "Low Priority": "text-red-400", "—": "text-slate-500" };
  const TYPE_BADGE   = { sme: "bg-teal-900/30 text-teal-400", project: "bg-purple-900/30 text-purple-400", corporate: "bg-blue-900/30 text-blue-400" };

  return (
    <div className="space-y-5" data-testid="admin-deals">
      <div>
        <h1 className="text-xl font-bold text-white">Deal & Program Oversight</h1>
        <p className="text-[11px] text-slate-500 mt-0.5">Admin clearance gate — validate, clear, or hold before FI visibility.</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-1 bg-[#0B1220] border border-white/8 rounded-xl p-1 w-fit">
        {[["all","All"],["flagged","⚠ Flagged"],["sme","SME"],["project","Project"],["corporate","Corporate"]].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${filter===v ? "bg-white/10 text-white" : "text-slate-500 hover:text-white"}`}
            data-testid={`deal-filter-${v}`}>{l}</button>
        ))}
      </div>

      {loading ? <Loader /> : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">{deals.length} deal{deals.length !== 1 ? "s" : ""} · {deals.filter(d=>d.is_flagged).length} flagged</p>
          {deals.map(d => (
            <div key={d.request_id} className={`bg-[#0B1220] border rounded-xl overflow-hidden ${d.is_flagged ? "border-red-500/30" : "border-white/8"}`}
              data-testid={`deal-row-${d.request_id}`}>
              <div className="flex items-start gap-4 px-5 py-3 cursor-pointer hover:bg-white/2"
                onClick={() => setExpanded(expanded === d.request_id ? null : d.request_id)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className="text-xs font-bold text-white">{d.org_name}</p>
                    <span className="text-xs font-mono text-slate-500">{d.request_id}</span>
                    {d.workspace_type && <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${TYPE_BADGE[d.workspace_type] || "bg-white/10 text-white"}`}>{d.workspace_type.toUpperCase()}</span>}
                    {d.is_flagged && <span className="text-xs font-bold text-red-400 bg-red-900/20 px-1.5 py-0.5 rounded-full">⚠ {d.flags.join(", ")}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className={SIGNAL_COLOR[d.deal_signal] || "text-slate-500"}>{d.deal_signal || "Unscored"}</span>
                    <span className="capitalize">{d.status?.replace(/_/g," ")}</span>
                    {d.amount > 0 && <span>${(d.amount/1000).toFixed(0)}k</span>}
                    {d.total_tco2e > 0 && <span>{d.total_tco2e}t</span>}
                  </div>
                </div>
                {/* Admin Clearance Gate */}
                {d.status === "submitted" && (
                  <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleAction(d.request_id, "clear")} disabled={!!acting}
                      className="text-xs font-bold px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40"
                      data-testid={`clear-${d.request_id}`}>
                      {acting === d.request_id+"clear" ? <RefreshCw size={10} className="animate-spin" /> : "Clear ✓"}
                    </button>
                    <button onClick={() => handleAction(d.request_id, "hold")} disabled={!!acting}
                      className="text-xs font-bold px-3 py-1.5 border border-amber-500/30 text-amber-400 rounded-lg hover:bg-amber-500/10 disabled:opacity-40">
                      Hold
                    </button>
                  </div>
                )}
              </div>
              {expanded === d.request_id && (
                <div className="border-t border-white/5 px-5 pb-4">
                  <DealRiskBadge requestId={d.request_id} />
                </div>
              )}
            </div>
          ))}
          {deals.length === 0 && <p className="text-xs text-slate-500 py-4">No deals match the current filter.</p>}
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════
// PAGE 5: Quality Control
// ═══════════════════════════════════════════════════════════════════════════
function AdminQuality() {
  return (
    <div className="space-y-5" data-testid="admin-quality">
      <div>
        <h1 className="text-xl font-bold text-white">Data Quality Control</h1>
        <p className="text-[11px] text-slate-500 mt-0.5">Platform credibility depends on data quality. Flag, annotate, or escalate.</p>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {[
          { label: "Emission Factor Compliance",  desc: "Verify all assessments use approved IPCC / GHG Protocol factors", action: "Review factors", color: "border-teal-500/30 bg-teal-500/5"  },
          { label: "Baseline Assumption Audit",   desc: "Flag assessments with weak or undocumented baseline assumptions", action: "View assessments", color: "border-amber-500/30 bg-amber-500/5" },
          { label: "Reduction Logic Validation",  desc: "Check project scenarios for unrealistic reduction claims (>90%)", action: "Run check", color: "border-red-500/30 bg-red-500/5"      },
          { label: "CAPEX Realism Check",         desc: "Flag projects where CAPEX per tCO₂e is outside typical range ($5–$200)", action: "Review", color: "border-purple-500/30 bg-purple-500/5" },
        ].map(q => (
          <div key={q.label} className={`border rounded-xl px-5 py-4 flex items-start justify-between gap-4 ${q.color}`}>
            <div>
              <p className="text-xs font-bold text-white mb-0.5">{q.label}</p>
              <p className="text-xs text-slate-400">{q.desc}</p>
            </div>
            <button className="flex-shrink-0 text-xs font-bold text-teal-400 border border-teal-500/30 px-3 py-1.5 rounded-lg hover:bg-teal-500/10 whitespace-nowrap">
              {q.action}
            </button>
          </div>
        ))}
      </div>
      <div className="bg-[#0B1220] border border-white/8 rounded-xl p-4">
        <p className="text-xs font-bold text-white mb-1">Quality Philosophy</p>
        <p className="text-xs text-slate-400 leading-relaxed">
          Admin does NOT modify user data directly. Quality control actions produce flags and annotations
          that are visible to users, prompting them to revise. This preserves data integrity and user trust.
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 6: System Config
// ═══════════════════════════════════════════════════════════════════════════
function AdminSystem() {
  return (
    <div className="space-y-5" data-testid="admin-system">
      <div>
        <h1 className="text-xl font-bold text-white">System Configuration</h1>
        <p className="text-[11px] text-slate-500 mt-0.5">Role definitions, presets, and platform parameters. Change with care.</p>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {[
          { label: "Role Definitions",  desc: "SME / Corporate / Project / FI — workspace access rules", status: "Active",    safe: true  },
          { label: "Country Presets",   desc: "Emission factors and NDC context per country",             status: "14 active", safe: true  },
          { label: "Sector Presets",    desc: "Industry classifications and benchmark ranges",             status: "8 sectors", safe: true  },
          { label: "Approval Mode",     desc: "Public signups require admin approval (currently: ON)",    status: "Enforced",  safe: false },
        ].map(s => (
          <div key={s.label} className="bg-[#0B1220] border border-white/8 rounded-xl px-5 py-4 flex items-center gap-4">
            <div className="flex-1">
              <p className="text-xs font-bold text-white">{s.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.safe ? "bg-teal-900/30 text-teal-400" : "bg-amber-900/30 text-amber-400"}`}>{s.status}</span>
              {s.safe ? <Settings size={12} className="text-slate-600" /> : <Lock size={12} className="text-amber-500" />}
            </div>
          </div>
        ))}
      </div>
      <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl px-4 py-3 flex items-start gap-2">
        <AlertTriangle size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-600">System configuration changes affect all users. Changes are logged and cannot be silently reversed.</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE 6: Audit Log
// ═══════════════════════════════════════════════════════════════════════════
function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [total,   setTotal]   = useState(0);
  const [filter,  setFilter]  = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`/admin-gov/audit-log${filter ? `?entity_type=${filter}` : ""}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const ACTION_COLOR = {
    deal_cleared:       "text-emerald-400",
    deal_held:          "text-amber-400",
    revision_requested: "text-amber-400",
    admin_role_changed: "text-blue-400",
    pipeline_stage:     "text-teal-400",
    user_approved:      "text-emerald-400",
    user_rejected:      "text-red-400",
  };
  const getColor = (action) => {
    const key = Object.keys(ACTION_COLOR).find(k => action?.includes(k));
    return key ? ACTION_COLOR[key] : "text-slate-400";
  };

  return (
    <div className="space-y-5" data-testid="admin-audit-log">
      <div>
        <h1 className="text-xl font-bold text-white">Audit Log</h1>
        <p className="text-[11px] text-slate-500 mt-0.5">
          Immutable record of all admin governance actions. Who did what, when.
        </p>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="h-9 px-3 bg-[#0B1220] border border-white/10 rounded-lg text-xs text-white focus:outline-none">
          <option value="">All entity types</option>
          {["finance_request","user","enterprise_inquiry"].map(t => (
            <option key={t} value={t} className="text-slate-800">{t.replace(/_/g," ")}</option>
          ))}
        </select>
        <button onClick={load} className="text-xs text-teal-400 hover:underline flex items-center gap-1">
          <RefreshCw size={11} /> Refresh
        </button>
        <p className="text-xs text-slate-500 ml-auto">{total} total entries</p>
      </div>

      {loading ? <Loader /> : entries.length === 0 ? (
        <div className="bg-[#0B1220] border border-white/8 rounded-xl p-8 text-center">
          <Clock size={20} className="text-slate-600 mx-auto mb-2" />
          <p className="text-xs text-slate-500">No audit entries yet. Admin actions are recorded here as they happen.</p>
        </div>
      ) : (
        <div className="bg-[#0B1220] border border-white/8 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-6 gap-4 px-5 py-2.5 border-b border-white/5 text-xs text-slate-600 uppercase tracking-widest font-bold">
            <span>Action</span><span>Admin</span><span>Role</span><span>Entity</span><span>Before → After</span><span>When</span>
          </div>
          {entries.map((e, i) => (
            <div key={e.id} className={`grid grid-cols-6 gap-4 px-5 py-3 text-[11px] ${i % 2 === 0 ? "bg-white/1" : ""} border-b border-white/3 last:border-0`}
              data-testid={`audit-entry-${e.id}`}>
              <span className={`font-semibold ${getColor(e.action)}`}>{e.action?.replace(/_/g," ")}</span>
              <span className="text-slate-400 truncate">{e.admin_email?.split("@")[0]}</span>
              <span className="text-slate-500 capitalize">{e.admin_role?.replace(/_/g," ")}</span>
              <span className="text-slate-500 truncate">{e.entity_type ? `${e.entity_type?.replace(/_/g," ")} ${e.entity_id || ""}` : "—"}</span>
              <span className="text-slate-500 text-xs">
                {e.before_state || e.after_state ? (
                  <span>
                    {e.before_state?.status && <span className="text-red-400/70">{e.before_state.status}</span>}
                    {e.before_state?.status && e.after_state?.status && <span className="text-slate-600"> → </span>}
                    {e.after_state?.status && <span className="text-emerald-400/70">{e.after_state.status}</span>}
                  </span>
                ) : <span className="text-slate-700">—</span>}
              </span>
              <span className="text-slate-600">{e.created_at ? new Date(e.created_at).toLocaleString("en-GB", {day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}) : "—"}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-600 italic">
        Audit log is append-only. Admin cannot delete or modify entries. All timestamps are UTC.
      </p>
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────
function Loader() {
  return <div className="flex items-center justify-center py-12"><RefreshCw size={16} className="animate-spin text-teal-500" /></div>;
}

// ═══════════════════════════════════════════════════════════════════════════
// ROOT: Admin Governance Layout
// ═══════════════════════════════════════════════════════════════════════════
export default function AdminGovernance() {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const [kpis,      setKpis]      = useState(null);
  const [snapshot,  setSnapshot]  = useState(null);
  const [deals,     setDeals]     = useState([]);

  useEffect(() => {
    if (user?.role !== "admin") { navigate("/dashboard"); return; }
    const load = async () => {
      const [k, s, d] = await Promise.allSettled([
        client.get("/admin-gov/kpis"),
        client.get("/admin-gov/platform-snapshot"),
        client.get("/admin-gov/deals"),
      ]);
      if (k.status === "fulfilled") setKpis(k.value.data);
      if (s.status === "fulfilled") setSnapshot(s.value.data);
      if (d.status === "fulfilled") setDeals(d.value.data?.deals || []);
    };
    load();
  }, [user, navigate]);

  return (
    <div className="flex h-screen bg-[#060D18]" data-testid="admin-governance">
      <AdminNav kpis={kpis} />
      <div className="flex-1 overflow-y-auto px-8 py-7">
          <Routes>
            <Route index                     element={<AdminDashboard kpis={kpis} snapshot={snapshot} deals={deals} />} />
            <Route path="dashboard"          element={<AdminDashboard kpis={kpis} snapshot={snapshot} deals={deals} />} />
            <Route path="users"              element={<AdminUsers />} />
            <Route path="onboarding"         element={<ClientActivationPage />} />
            <Route path="enterprise-pipeline"element={<AdminEnterprisePipeline />} />
            <Route path="deals"              element={<AdminDeals />} />
            <Route path="quality"            element={<AdminQuality />} />
            <Route path="audit-log"          element={<AdminAuditLog />} />
            <Route path="system"             element={<AdminSystem />} />
          </Routes>
        </div>
    </div>
  );
}
